<template>
	<div
		class="hotel-list-item-review"
		:class="{ 'hotel-list-item-review--no-description': ratingDescription === '' }"
	>
		<div class="hotel-list-item-review__ratings">
			<div class="hotel-list-item-review__rating">
				{{ overallRating }}
			</div>
			<div class="hotel-list-item-review__percentage">{{ recommendationRate }}%</div>
		</div>
		<div>
			<p class="hotel-list-item-review__description">
				{{ ratingDescription }}
			</p>
			<p class="hotel-list-item-review__rating-count">
				{{ ratingCount }}
			</p>
			<p class="hotel-list-item-review__recommendation-rate">Weiterempfehlungsrate</p>
		</div>
	</div>
</template>
<script lang="ts" setup>
import type { ItemType } from '@/interfaces/hotel-list-types/hotel-list-types';
import { pluralize, ratingText } from '@/js/utils/utils';
import { numberFormat } from '@/js/utils/numberUtils';
import { computed } from 'vue';
import { ratingOverall } from '@/js/utils/utils';

type Props = {
	review: Pick<ItemType, 'Ratings' | 'NumberOfRatings'>;
};

const { review } = defineProps<Props>();

const ratingDescription = ratingText(review.Ratings.Overall);

const ratingCount = computed(() => `${pluralize(review.NumberOfRatings, 'Bewertung', 'Bewertungen')}`);

const overallRating = ratingOverall(review.Ratings.Overall);

const recommendationRate = computed(() => numberFormat(review.Ratings.RecommendationRate ?? 0, 0));
</script>
<style lang="scss" scoped>
.hotel-list-item-review {
	display: flex;
	column-gap: 0.8rem;
	align-items: center;

	&--no-description {
		align-items: flex-end;
	}

	&__ratings {
		border: solid 0.1rem $color-review-primary;
		border-radius: $border-radius-small;
		font-weight: $font-weight-semibold;
		width: 4.2rem;
	}

	&__rating {
		background-color: $color-review-primary;
		color: $color-white;
		padding: 0.2rem 0.4rem;
		text-align: center;
		font-size: $font-small-2;
		line-height: 2rem;
	}

	&__percentage {
		padding: 0.2rem 0.4rem;
		color: $color-review-primary;
		text-align: center;
		font-size: $font-small-3;
		line-height: 2rem;
	}

	&__description {
		color: $color-review-primary;
		font-size: $font-small-1;
		font-weight: $font-weight-semibold;
		margin: 0;
		line-height: 2rem;
	}

	&__rating-count {
		color: $color-text-light;
		font-weight: $font-weight-regular;
		font-size: $font-small-4;
		margin: 0;
		line-height: 1.429rem;
		min-height: 1.5rem;
	}

	&__recommendation-rate {
		color: $color-text-light;
		font-size: $font-small-3;
		font-weight: $font-weight-regular;
		margin: 0;
		line-height: 1.429rem;
		min-height: 1.5rem;
	}
}

// a bit smaller than $breakpoint-small, it's the width of the tablet component in the design
@container hotel_item_list (min-width: 72.5rem) {
	.hotel-list-item-review {
		&__ratings {
			width: 6.2rem;
		}

		&__rating {
			padding: 0.4rem 0.8rem;
			font-size: $font-medium-3;
		}

		&__percentage {
			padding: 0.4rem 0.8rem;
			font-size: $font-small-1;
		}

		&__description {
			font-size: $font-small-1;
			font-weight: $font-weight-semibold;
			margin: 0;
		}

		&__rating-count {
			font-size: $font-small-2;
			line-height: 1.8rem;
		}

		&__recommendation-rate {
			font-size: $font-small-2;
			line-height: 1.8rem;
		}
	}
}

// Here is a lot of c/p'd code from the @container declarations above.
// This is necessary, because Mozilla Firefox processes @container differently (not at all)
// for Print Previews in comparison to Chrome and Edge
// Once Mozilla decides to change this we can simplify this section (i believe this whole block could be removed in that case)
@media print {
	.hotel-list-item-review {
		&__ratings {
			width: 6.2rem;
			overflow: hidden;
		}

		&__rating {
			padding: 0.4rem 0.8rem;
			font-size: $font-medium-3;
		}

		&__percentage {
			padding: 0.4rem 0.8rem;
			font-size: $font-small-1;
		}

		&__description {
			font-size: $font-small-1;
			font-weight: $font-weight-semibold;
			margin: 0;
		}

		&__rating-count {
			font-size: $font-small-2;
			line-height: 1.8rem;
		}

		&__recommendation-rate {
			font-size: $font-small-2;
			line-height: 1.8rem;
		}
	}
}
</style>
